/**
 * Phase 13 — per-employee Claude Code plugin (MCP) setup page.
 *
 * Lives at `/my-mcp-setup`. Wrapped in the same legacy chrome as the rest of
 * the seller-portal sub-pages (`PageWrapperMain` → `SubPageContentHolder` →
 * `SubPageInnerContentHolder`) so the sidebar stays mounted. The body uses
 * custom-SCSS cards (see `src/styles/pages/mcp-setup/mcpSetup.scss`) — no
 * MUI `Paper`/`Alert`/`Snackbar`. Toasts go through the legacy `setMessage`
 * Redux action.
 *
 * Journey B — the page reads as a single confidence-inspiring flow:
 *   Step 1  Copy your key       — show + copy + regenerate (two-step confirm).
 *   Step 2  Paste into Claude    — ready-to-paste `.mcp.json` snippet + install.
 *   Step 3  Test connection      — hits `GET /api/v1/me`; prominent green
 *                                  "✓ Connected as {name} ({role})" success or a
 *                                  red, actionable failure with a one-line fix.
 * A troubleshooting disclosure sits at the bottom for when the check fails.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import '../../styles/pages/mcp-setup/mcpSetup.scss';

import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import KeySVG from '../../ui/customSVGs/KeySVG';
import BlurAndCopyInput from '../../ui/inputs/BlurAndCopyInput';
import { RootState } from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import { setAgencyAccessToken } from '../../store/reducers/authSlice';
import { APP_ENV } from '../../lib/constants/central-api';
import {
  getConnectorAddress,
  getCustomConnectorAddress,
} from '../../lib/utils/connector-address';
import {
  fetchIdentity,
  listMcpOAuthSessions,
  McpOAuthSession,
  regenerateOwnApiKey,
  revokeMcpOAuthSession,
} from '../../lib/api/identity/identity-api';
import {
  keySVG,
  copySVG,
  tickSVG,
  crossSVG,
  reloadArrowsSVG,
  eyeSVG,
  eyeCrossedSVG,
} from '../../lib/allCustomSVGs';


const PLUGIN_INSTALL_CMD = 'npm install -g @adup/claude-plugin';
const MCP_CONFIG_PATH = '~/.config/claude-code/mcp.json';
const PLUGIN_MARKETPLACE_URL = 'https://github.com/Ad-Up-VC/adup-claude-plugin';
const KEY_PLACEHOLDER = 'YOUR_KEY_HERE';
/** Stand-in for the key in the rendered snippet until the user reveals it. */
const KEY_MASK = '••••••••••••••••••••••••';

type TestResult =
  | { state: 'idle' }
  | { state: 'pending' }
  | { state: 'success'; name: string; role: string }
  | { state: 'error'; message: string };

const McpSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const agencyToken = useSelector(
    (state: RootState) => state.auth.agency.accessToken
  );
  const employeeIdentity = useSelector(
    (state: RootState) => state.auth.agency.employeeIdentity
  );

  const hasToken = !!agencyToken && agencyToken.length > 0;
  const gatewayUrl = getConnectorAddress(APP_ENV);
  const customServersUrl = getCustomConnectorAddress(gatewayUrl);

  // Single connector: the gateway's base /mcp aggregates every connected
  // platform's tools for the active shop (namespaced platform__tool), so one
  // connector + one key serves everything (tara-gateway PR #57).
  const buildSnippet = useCallback(
    (keyValue: string) =>
      JSON.stringify(
        {
          mcpServers: {
            adup: {
              type: 'http',
              url: gatewayUrl,
              headers: { Authorization: `Bearer ${keyValue}` },
            },
          },
        },
        null,
        2
      ),
    [gatewayUrl]
  );

  // The real snippet — never rendered while the key is hidden, but it is what
  // "Copy snippet" puts on the clipboard, so setup still works in one click.
  const snippet = useMemo(
    () => buildSnippet(hasToken ? agencyToken : KEY_PLACEHOLDER),
    [agencyToken, buildSnippet, hasToken]
  );

  // The key stays masked in the rendered snippet until the user asks for it,
  // so a shared screen, a screencast or a support screenshot never leaks it.
  const [isSnippetRevealed, setIsSnippetRevealed] = useState(false);
  const displayedSnippet = useMemo(
    () => (!hasToken || isSnippetRevealed ? snippet : buildSnippet(KEY_MASK)),
    [buildSnippet, hasToken, isSnippetRevealed, snippet]
  );

  const [testResult, setTestResult] = useState<TestResult>({ state: 'idle' });
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  // Regenerate is destructive (it kills the key the user's plugin is holding),
  // so it is armed by a first click and committed by a second.
  const [regenerateArmed, setRegenerateArmed] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // ----- Connected devices (MCP-client OAuth, TARA_PLUGIN_OAUTH2_PLAN.md WS3) -----
  // One entry per OAuth authorization (= per device that signed in from
  // Claude). `null` = not loaded yet. Revoke is two-step like Regenerate: it
  // signs Claude out on that device immediately.
  const [sessions, setSessions] = useState<McpOAuthSession[] | null>(null);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [revokeArmedFamily, setRevokeArmedFamily] = useState<string | null>(null);
  const [revokingFamily, setRevokingFamily] = useState<string | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    dispatch(setMessage({ messageType: type, message: msg }));
  };

  const loadSessions = useCallback(async () => {
    try {
      const list = await listMcpOAuthSessions();
      setSessions(list);
      setSessionsError(null);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn('[McpSetup] loading connected devices failed', err);
      setSessions([]);
      setSessionsError(err?.message || t('McpSetup_DevicesLoadFailed'));
    }
  }, [t]);

  useEffect(() => {
    if (hasToken) {
      loadSessions();
    }
  }, [hasToken, agencyToken, loadSessions]);

  const handleRevoke = async (familyId: string) => {
    if (revokeArmedFamily !== familyId) {
      setRevokeArmedFamily(familyId);
      return;
    }
    setRevokeArmedFamily(null);
    setRevokingFamily(familyId);
    try {
      await revokeMcpOAuthSession(familyId);
      showToast(t('McpSetup_DeviceRevoked'), 'success');
      await loadSessions();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn('[McpSetup] revoke failed', err);
      showToast(err?.message || t('McpSetup_DeviceRevokeFailed'), 'error');
    } finally {
      setRevokingFamily(null);
    }
  };

  const formatWhen = (iso: string | null) => {
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
  };

  const handleCopy = async (value: string, successLabel: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showToast(successLabel, 'success');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[McpSetup] clipboard write failed', err);
      showToast(t('McpSetup_CopyFailed'), 'error');
    }
  };

  /**
   * Rotate the employee's `emp_` key.
   *
   * Two-step on purpose. Signing in no longer rotates the key, which makes THIS
   * the only thing that can break a working plugin/connector — so it must never
   * fire on a stray click. The first press arms, the second commits.
   */
  const handleRegenerate = async () => {
    if (!regenerateArmed) {
      setRegenerateArmed(true);
      return;
    }

    setRegenerateArmed(false);
    setIsRegenerating(true);

    try {
      const { api_key } = await regenerateOwnApiKey();

      // Push the new key into Redux so the snippet, the copy button and the
      // test-connection check all reflect it immediately. Redux only — never
      // web storage; the httpOnly cookie the response set is the durable half.
      dispatch(setAgencyAccessToken(api_key));

      // Any previous green tick refers to the key we just invalidated.
      setTestResult({ state: 'idle' });

      showToast(t('McpSetup_RegenerateSuccess'), 'success');
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn('[McpSetup] regenerate failed', err);
      showToast(err?.message || t('McpSetup_RegenerateFailed'), 'error');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleTestConnection = async () => {
    if (!hasToken) {
      setTestResult({ state: 'error', message: t('McpSetup_TestNoKey') });
      return;
    }
    setTestResult({ state: 'pending' });
    try {
      const identity = await fetchIdentity(agencyToken);
      setTestResult({
        state: 'success',
        name: identity.name || identity.email || t('User'),
        role: identity.role || identity.type || '—',
      });
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn('[McpSetup] test connection failed', err);
      setTestResult({
        state: 'error',
        message: err?.message || t('McpSetup_TestFailedFallback'),
      });
      // A failure is the moment the troubleshooting tips are most useful —
      // surface them automatically so the fix is one glance away.
      setShowTroubleshooting(true);
    }
  };

  /* ============================================================
     Not-logged-in state — still mount the full chrome so the
     sidebar is visible and the user can navigate elsewhere.
     ============================================================ */
  if (!hasToken) {
    return (
      <PageWrapperMain>
        <div className='subPage'>
          <SubPageContentHolder>
            <SubPageInnerContentHolder
              title={t('MyMcpSetup')}
              subTitle={t('McpSetup_HeroBody')}
              backgroundColor={'var(--main-background-color)'}
            >
              <div className='mcpSetup'>
                <div className='mcpSetup__loginRequired'>
                  <span>{t('McpSetup_LoginRequired')}</span>
                  <MainSquareButton
                    type='button'
                    value={t('SignIn')}
                    buttonType='white'
                    normal={true}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/login');
                    }}
                  />
                </div>
              </div>
            </SubPageInnerContentHolder>
          </SubPageContentHolder>
        </div>
      </PageWrapperMain>
    );
  }

  /* ============================================================
     Main view — a single "copy → paste → test → green check" flow.
     ============================================================ */
  return (
    <PageWrapperMain>
      <div className='subPage'>
        <SubPageContentHolder>
          <SubPageInnerContentHolder
            title={t('MyMcpSetup')}
            subTitle={t('McpSetup_HeroBody')}
            backgroundColor={'var(--main-background-color)'}
          >
            <div className='mcpSetup'>
              {/* At-a-glance flow summary so the page reads in one breath. */}
              <p className='mcpSetup__flowHint'>
                {t('McpSetup_FlowHint')}
              </p>

              {/* ---------- Sign in from Claude + Connected devices ---------- */}
              <section className='mcpSetup__step mcpSetup__step--devices'>
                <div className='mcpSetup__stepHead'>
                  <div className='mcpSetup__stepHeadText'>
                    <h3 className='mcpSetup__sectionTitle'>
                      {t('McpSetup_DevicesTitle')}
                    </h3>
                    <p className='mcpSetup__sectionBody'>
                      {t('McpSetup_SignInHint')}
                    </p>
                    <p className='mcpSetup__sectionBody'>
                      {t('McpSetup_DevicesBody')}
                    </p>
                  </div>
                </div>
                {sessionsError && (
                  <p className='mcpSetup__note mcpSetup__note--warn'>
                    {sessionsError}
                  </p>
                )}
                {sessions !== null && sessions.length === 0 && !sessionsError && (
                  <p className='mcpSetup__note'>{t('McpSetup_DevicesEmpty')}</p>
                )}
                {sessions !== null && sessions.length > 0 && (
                  <ul className='mcpSetup__devices'>
                    {sessions.map((session) => {
                      const armed = revokeArmedFamily === session.family_id;
                      const busy = revokingFamily === session.family_id;
                      const since = formatWhen(session.created_at);
                      const lastUsed = formatWhen(session.last_used_at);
                      return (
                        <li key={session.family_id} className='mcpSetup__device'>
                          <div className='mcpSetup__deviceText'>
                            <strong className='mcpSetup__deviceName'>
                              {session.client_name || t('McpSetup_DeviceUnknownClient')}
                              {session.is_current && (
                                <span className='mcpSetup__deviceBadge'>
                                  {t('McpSetup_DeviceCurrent')}
                                </span>
                              )}
                            </strong>
                            <span className='mcpSetup__deviceMeta'>
                              {since && t('McpSetup_DeviceSince', { date: since })}
                              {since && ' · '}
                              {lastUsed
                                ? t('McpSetup_DeviceLastUsed', { date: lastUsed })
                                : t('McpSetup_DeviceNeverUsed')}
                              {session.ip_hint && ` · ${session.ip_hint}`}
                            </span>
                          </div>
                          <div className='mcpSetup__deviceActions'>
                            <MainSquareButton
                              type='button'
                              value={
                                busy
                                  ? t('McpSetup_DeviceRevoking')
                                  : armed
                                  ? t('McpSetup_DeviceRevokeConfirm')
                                  : t('McpSetup_DeviceRevoke')
                              }
                              buttonType={armed ? 'danger' : 'white'}
                              normal={true}
                              disabled={busy}
                              onClick={(e) => {
                                e.preventDefault();
                                handleRevoke(session.family_id);
                              }}
                            />
                            {armed && !busy && (
                              <MainSquareButton
                                type='button'
                                value={t('Cancel')}
                                buttonType='white'
                                normal={true}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setRevokeArmedFamily(null);
                                }}
                              />
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {/* ---------- Step 1: Copy your key ---------- */}
              <section className='mcpSetup__step'>
                <div className='mcpSetup__stepHead'>
                  <span className='mcpSetup__stepNum' aria-hidden='true'>
                    1
                  </span>
                  <div className='mcpSetup__stepHeadText'>
                    <h3 className='mcpSetup__sectionTitle'>
                      {t('McpSetup_Step1Title')}
                    </h3>
                    <p className='mcpSetup__sectionBody'>
                      {t('McpSetup_ApiKeyBody')}
                    </p>
                  </div>
                </div>
                <div className='mcpSetup__stepBodyIndent'>
                  {/* Masked by default (the repo's blur-and-copy field) so the
                      key is never readable on a shared screen; the eye toggle
                      reveals it and "Copy key" always copies the real one. */}
                  <div className='mcpSetup__keyField'>
                    <BlurAndCopyInput
                      icon={<KeySVG fill='#888888' />}
                      id='mcpSetup-agency-key'
                      value={agencyToken}
                      placeholder={t('McpSetup_KeyFieldLabel')}
                      readonly={true}
                      isCopyToClipboardEnabled={true}
                    />
                  </div>
                  <div className='mcpSetup__buttons'>
                    <MainSquareButton
                      type='button'
                      value={t('McpSetup_CopyKey')}
                      icon={keySVG}
                      normal={true}
                      onClick={(e) => {
                        e.preventDefault();
                        handleCopy(agencyToken, t('McpSetup_KeyCopied'));
                      }}
                    />
                    <MainSquareButton
                      type='button'
                      value={
                        isRegenerating
                          ? t('McpSetup_Regenerating')
                          : regenerateArmed
                          ? t('McpSetup_RegenerateConfirm')
                          : t('McpSetup_RegenerateKey')
                      }
                      buttonType={regenerateArmed ? 'danger' : 'white'}
                      normal={true}
                      disabled={isRegenerating}
                      icon={reloadArrowsSVG}
                      message={t('McpSetup_RegenerateTooltip')}
                      onClick={(e) => {
                        e.preventDefault();
                        handleRegenerate();
                      }}
                    />
                    {regenerateArmed && !isRegenerating && (
                      <MainSquareButton
                        type='button'
                        value={t('Cancel')}
                        buttonType='white'
                        normal={true}
                        onClick={(e) => {
                          e.preventDefault();
                          setRegenerateArmed(false);
                        }}
                      />
                    )}
                  </div>
                  {regenerateArmed && (
                    <p className='mcpSetup__note mcpSetup__note--warn'>
                      {t('McpSetup_RegenerateWarning')}
                    </p>
                  )}
                  <p className='mcpSetup__note mcpSetup__note--warn'>
                    {t('McpSetup_KeySecrecyNote')}
                  </p>
                </div>
              </section>

              {/* ---------- Step 2: Paste into Claude config ---------- */}
              <section className='mcpSetup__step'>
                <div className='mcpSetup__stepHead'>
                  <span className='mcpSetup__stepNum' aria-hidden='true'>
                    2
                  </span>
                  <div className='mcpSetup__stepHeadText'>
                    <h3 className='mcpSetup__sectionTitle'>
                      {t('McpSetup_Step2Title')}
                    </h3>
                    <p className='mcpSetup__sectionBody'>
                      {t('McpSetup_Step2Body')}
                    </p>
                  </div>
                </div>
                <div className='mcpSetup__stepBodyIndent'>
                  {/* Recommended path: add the plugin marketplace by GitHub URL */}
                  <div className='mcpSetup__marketplace'>
                    <h4 className='mcpSetup__sectionTitle'>
                      {t('McpSetup_MarketplaceTitle')}
                    </h4>
                    <ol className='mcpSetup__substeps'>
                      <li>{t('McpSetup_MarketplaceStep1')}</li>
                      <li>
                        {t('McpSetup_MarketplaceStep2')}{' '}
                        <code className='mcpSetup__inlineCode'>
                          {PLUGIN_MARKETPLACE_URL}
                        </code>
                      </li>
                      <li>{t('McpSetup_MarketplaceStep3')}</li>
                    </ol>
                    <div className='mcpSetup__buttons'>
                      <MainSquareButton
                        type='button'
                        value={t('McpSetup_CopyUrl')}
                        icon={copySVG}
                        normal={true}
                        onClick={(e) => {
                          e.preventDefault();
                          handleCopy(
                            PLUGIN_MARKETPLACE_URL,
                            t('McpSetup_UrlCopied')
                          );
                        }}
                      />
                    </div>
                    <p className='mcpSetup__note'>
                      {t('McpSetup_MarketplaceNote')}
                    </p>
                  </div>
                  {/* Rendered with the key masked; the copy button below still
                      copies the snippet with the real key in it. */}
                  <pre className='mcpSetup__codeBlock'>
                    <code>{displayedSnippet}</code>
                  </pre>
                  <div className='mcpSetup__buttons'>
                    <MainSquareButton
                      type='button'
                      value={t('McpSetup_CopySnippet')}
                      icon={copySVG}
                      normal={true}
                      onClick={(e) => {
                        e.preventDefault();
                        handleCopy(snippet, t('McpSetup_SnippetCopied'));
                      }}
                    />
                    {hasToken && (
                      <MainSquareButton
                        type='button'
                        value={
                          isSnippetRevealed
                            ? t('McpSetup_HideKeyInSnippet')
                            : t('McpSetup_ShowKeyInSnippet')
                        }
                        buttonType='white'
                        icon={isSnippetRevealed ? eyeCrossedSVG : eyeSVG}
                        normal={true}
                        onClick={(e) => {
                          e.preventDefault();
                          setIsSnippetRevealed((prev) => !prev);
                        }}
                      />
                    )}
                  </div>
                  {hasToken && !isSnippetRevealed && (
                    <p className='mcpSetup__note'>
                      {t('McpSetup_SnippetMaskedNote')}
                    </p>
                  )}
                  {/* Custom servers are not part of the base connector: the
                      gateway serves them on their own address. */}
                  <div
                    className='mcpSetup__customAddress'
                    data-testid='mcpSetup-customAddress'
                  >
                    <h4 className='mcpSetup__sectionTitle'>
                      {t('CustomServersAddress_Label')}
                    </h4>
                    <code className='mcpSetup__inlineCode'>
                      {customServersUrl}
                    </code>
                    <div className='mcpSetup__buttons'>
                      <MainSquareButton
                        type='button'
                        value={t('CustomServersAddress_Copy')}
                        icon={copySVG}
                        buttonType='white'
                        normal={true}
                        onClick={(e) => {
                          e.preventDefault();
                          handleCopy(
                            customServersUrl,
                            t('CustomServersAddress_Copied')
                          );
                        }}
                      />
                    </div>
                    <p className='mcpSetup__note'>
                      {t('CustomServersAddress_Note')}
                    </p>
                  </div>
                  <ol className='mcpSetup__substeps'>
                    <li>
                      {t('McpSetup_InstallStep1')}{' '}
                      <code className='mcpSetup__inlineCode'>
                        {PLUGIN_INSTALL_CMD}
                      </code>
                    </li>
                    <li>
                      {t('McpSetup_InstallStep2Label')}{' '}
                      <code className='mcpSetup__inlineCode'>
                        {MCP_CONFIG_PATH}
                      </code>
                    </li>
                    <li>{t('McpSetup_InstallStep3')}</li>
                  </ol>
                  <p className='mcpSetup__note'>{t('McpSetup_InstallNote')}</p>
                </div>
              </section>

              {/* ---------- Step 3: Test connection ---------- */}
              <section className='mcpSetup__step'>
                <div className='mcpSetup__stepHead'>
                  <span className='mcpSetup__stepNum' aria-hidden='true'>
                    3
                  </span>
                  <div className='mcpSetup__stepHeadText'>
                    <h3 className='mcpSetup__sectionTitle'>
                      {t('McpSetup_Step3Title')}
                    </h3>
                    <p className='mcpSetup__sectionBody'>
                      {t('McpSetup_TestBody')}
                    </p>
                  </div>
                </div>
                <div className='mcpSetup__stepBodyIndent'>
                  <div className='mcpSetup__buttons'>
                    <MainSquareButton
                      type='button'
                      value={t('McpSetup_TestButton')}
                      buttonType='success'
                      normal={true}
                      loading={testResult.state === 'pending'}
                      disabled={testResult.state === 'pending'}
                      onClick={(e) => {
                        e.preventDefault();
                        handleTestConnection();
                      }}
                    />
                  </div>

                  {testResult.state === 'success' && (
                    <div className='mcpSetup__result mcpSetup__result--success'>
                      <span
                        className='mcpSetup__result-icon'
                        aria-hidden='true'
                      >
                        <CustomSVGs svg={tickSVG} />
                      </span>
                      <div className='mcpSetup__result-text'>
                        <strong className='mcpSetup__result-title'>
                          {t('McpSetup_TestSuccessTitle')}
                        </strong>
                        <span>
                          {t('McpSetup_TestSuccess', {
                            name: testResult.name,
                            role: testResult.role,
                          })}
                        </span>
                      </div>
                    </div>
                  )}

                  {testResult.state === 'error' && (
                    <div className='mcpSetup__result mcpSetup__result--error'>
                      <span
                        className='mcpSetup__result-icon'
                        aria-hidden='true'
                      >
                        <CustomSVGs svg={crossSVG} />
                      </span>
                      <div className='mcpSetup__result-text'>
                        <strong className='mcpSetup__result-title'>
                          {t('McpSetup_TestErrorTitle')}
                        </strong>
                        <span>{testResult.message}</span>
                        <span className='mcpSetup__result-hint'>
                          {t('McpSetup_TestErrorHint')}
                        </span>
                      </div>
                    </div>
                  )}

                  {employeeIdentity?.email && (
                    <p className='mcpSetup__note'>
                      {t('McpSetup_LoggedInAs', {
                        email: employeeIdentity.email,
                      })}
                    </p>
                  )}
                </div>
              </section>

              {/* ---------- Troubleshooting disclosure ---------- */}
              <div className='mcpSetup__troubleshoot'>
                <button
                  type='button'
                  className='mcpSetup__troubleshoot-toggle'
                  aria-expanded={showTroubleshooting}
                  onClick={() => setShowTroubleshooting((v) => !v)}
                >
                  <span className='mcpSetup__troubleshoot-chevron' aria-hidden='true'>
                    {showTroubleshooting ? '▾' : '▸'}
                  </span>
                  {t('McpSetup_TroubleshootTitle')}
                </button>
                {showTroubleshooting && (
                  <ul className='mcpSetup__troubleshoot-list'>
                    <li>{t('McpSetup_TroubleshootTip1')}</li>
                    <li>{t('McpSetup_TroubleshootTip2')}</li>
                    <li>{t('McpSetup_TroubleshootTip3')}</li>
                    <li>{t('McpSetup_TroubleshootTip4')}</li>
                  </ul>
                )}
              </div>
            </div>
          </SubPageInnerContentHolder>
        </SubPageContentHolder>
      </div>
    </PageWrapperMain>
  );
};

export default McpSetup;
